"use client";

import { useState } from "react";

import classNames from "classnames";

import { Card, Separator } from "@jti/core-components";
import { type Config, getRegionIdCookie, setRegionIdCookie } from "@jti/fe-common";
import { RedirectionLinkCard } from "@jti/ui/components/RedirectionLinkCard";
import { Typography } from "@jti/ui/components/Typography";
import { useTranslations } from "@jti/ui/providers/useTranslations";

import type { RegionSelectorProps } from "./RegionSelector.builder";

import styles from "./RegionSelector.module.scss";

type RegionOptions = Config["regionSelector"]["regionOptions"];

interface RegionSelectorRendererProps extends RegionSelectorProps {
  selectedRegion: string;
  regionOptions: RegionOptions;
}
export function RegionSelectorRenderer({ selectedRegion, regionOptions }: RegionSelectorRendererProps) {
  const { __ } = useTranslations();

  const [collapsed, setCollapsed] = useState(true);

  const handleSelect = (regionId: string) => {
    const regionIdCookie = getRegionIdCookie();

    if (regionIdCookie === regionId) {
      return;
    }

    setRegionIdCookie(regionId);
    window.location.reload();
  };

  const selectedRegionName = regionOptions.find((option) => option.id === selectedRegion)?.regionName;

  return (
    <div className={styles.root}>
      <div className={styles.container}>
        <Card
          title={__("shop.region.selector.label")}
          isCollapsible={true}
          isCollapsed={collapsed}
          onToggle={() => setCollapsed(!collapsed)}
          showSeparator={true}
          variant="raised"
        >
          <div className={styles.content} role="group" aria-label={__("shop.region.selector.label")}>
            {regionOptions.map(({ regionName, id: regionId }) => (
              <RedirectionLinkCard
                key={regionName}
                variant="button"
                title={regionName}
                ariaPressed={regionId === selectedRegion}
                className={classNames(styles.option, regionId === selectedRegion && styles.selectedCard)}
                onClick={() => handleSelect(regionId)}
              />
            ))}
          </div>
        </Card>
        {collapsed && <Separator />}
      </div>
      {collapsed && (
        <Typography variant="body2" weight="light" as="span">
          {selectedRegionName ?? ""}
        </Typography>
      )}
    </div>
  );
}
